/**
 * Users Service for CoinVAT API
 * Handles user profile management, statistics, activity, and security
 */
import { apiClient } from './api-client';
import type {
  UserProfile,
  UserProfileUpdate,
  UserActivityParams,
} from './types';

export class UsersService {
  /**
   * Get current user's profile
   */
  async getCurrentUser(): Promise<UserProfile> {
    return apiClient.get<UserProfile>('/api/v1/users/me');
  }

  /**
   * Update current user's profile
   */
  async updateProfile(userData: UserProfileUpdate): Promise<UserProfile> {
    return apiClient.put<UserProfile>('/api/v1/users/me', userData);
  }

  /**
   * Delete current user's account (soft delete)
   */
  async deleteAccount(): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>('/api/v1/users/me');
  }

  /**
   * Upload profile picture/avatar
   */
  async uploadAvatar(file: File): Promise<{ profile_picture_url: string; message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    return apiClient.post<{ profile_picture_url: string; message: string }>(
      '/api/v1/users/upload-avatar',
      formData
    );
  }

  /**
   * Get user statistics and metrics
   */
  async getStats(): Promise<{
    total_wallets: number;
    total_exchanges: number;
    total_transactions: number;
    total_balance_usd: number;
    portfolio_performance: Record<string, any>;
    recent_activity_count: number;
  }> {
    return apiClient.get('/api/v1/users/stats');
  }

  /**
   * Get user activity log
   */
  async getActivity(params?: UserActivityParams): Promise<{
    activities: Array<{
      id: number;
      activity_type: string;
      description: string;
      metadata: Record<string, any>;
      ip_address: string;
      user_agent: string;
      created_at: string;
    }>;
    total_count: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/api/v1/users/activity?${queryString}` : '/api/v1/users/activity';
    
    return apiClient.get(endpoint);
  }

  /**
   * Get user security settings
   */
  async getSecuritySettings(): Promise<{
    two_factor_enabled: boolean;
    email_verified: boolean;
    phone_verified: boolean;
    login_notifications_enabled: boolean;
    api_access_enabled: boolean;
    last_password_change: string | null;
    active_sessions: Array<{
      id: string;
      device_info: string;
      ip_address: string;
      last_activity: string;
      is_current: boolean;
    }>;
  }> {
    return apiClient.get('/api/v1/users/security');
  }

  /**
   * Request email verification
   */
  async requestEmailVerification(): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/api/v1/users/verify-email');
  }

  /**
   * Verify email address with token
   */
  async verifyEmail(token: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(`/api/v1/users/verify-email/${token}`);
  }

  // Additional utility methods for user management

  /**
   * Check if current user's email is verified
   */
  async isEmailVerified(): Promise<boolean> {
    try {
      const profile = await this.getCurrentUser();
      return profile.email_verified;
    } catch (error) {
      console.error('Error checking email verification status:', error);
      return false;
    }
  }

  /**
   * Check if current user is premium
   */
  async isPremium(): Promise<boolean> {
    try {
      const profile = await this.getCurrentUser();
      return profile.is_premium;
    } catch (error) {
      console.error('Error checking premium status:', error);
      return false;
    }
  }

  /**
   * Get user's display name (username, full name, or email)
   */
  async getDisplayName(): Promise<string> {
    try {
      const profile = await this.getCurrentUser();
      
      if (profile.username) {
        return profile.username;
      }
      
      if (profile.first_name || profile.last_name) {
        return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
      }
      
      return profile.email;
    } catch (error) {
      console.error('Error getting display name:', error);
      return 'User';
    }
  }
}

// Export singleton instance
export const usersService = new UsersService();